// Import components
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

// Import services and shared
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { NetPingDeviceService } from '@zaoblako/web/core/services/control/net-ping-device.service';

// Import shared
import type { NetPingDeviceFindManyQueryInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find-many-query.dto';
import type { NetPingDeviceFindInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find.dto';

// Import externals
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  ViewChild,
  Input,
  Output,
  type OnInit
} from '@angular/core';

@Component({
  selector: 'crm-net-ping-device-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, SpinnerComponent],
  templateUrl: './net-ping-device-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NetPingDeviceMultiSelectComponent implements OnInit {
  @ViewChild('multiSelectComponent', { static: true }) public readonly multiSelectComponent: MultiSelectComponent;

  @Input() public netPingDevices: NetPingDeviceFindInterface[] = [];
  public queryParams: NetPingDeviceFindManyQueryInterface = {
    limit: 40,
    offset: 0,
    select: [
      'id',
      'name'
    ]
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedNetPingDevices: NetPingDeviceFindInterface[] = [];
  @Input() public set selectedNetPingDevices(selectedNetPingDevices: NetPingDeviceFindInterface[]) {
    if (this.customSearch || !this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.PHYSICAL_ACCESS_CONTROL?.enabled) {
      this._selectedNetPingDevices = selectedNetPingDevices;
      return;
    }

    if (
      selectedNetPingDevices?.every((selectedNetPingDevice) =>
        this.netPingDevices.some((netPingDevice) => netPingDevice.id === selectedNetPingDevice.id)
      )
    ) {
      this._selectedNetPingDevices = this.netPingDevices.filter((netPingDevice) =>
        selectedNetPingDevices?.some((selectedNetPingDevice) => netPingDevice.id === selectedNetPingDevice.id)
      );
      return;
    }

    const ids = selectedNetPingDevices
      ?.filter((selectedNetPingDevice) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === selectedNetPingDevice.id))
      .map((selectedNetPingDevice) => selectedNetPingDevice.id);
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }
    if (ids?.length) {
      this.netPingDeviceService
        .findMany({ includeIds: ids, limit: ids?.length > 500 ? 500 : ids?.length, offset: 0 })
        .subscribe({
          next: (data) => {
            this.netPingDevices = this.netPingDevices.concat(
              data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id))
            );
            this.cdRef.markForCheck();
            setTimeout(() => {
              this._selectedNetPingDevices = this.netPingDevices.filter((netPingDevice) => ids.includes(netPingDevice.id));
              this.cdRef.markForCheck();
            }, 30);
          }
        });
    }
  }

  @Output() public selectedNetPingDevicesChange = new EventEmitter<NetPingDeviceFindInterface[]>();

  @Input() public isError = false;

  @Input() public label: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public placeholder = '\u00A0';
  @Input() public itemStyleClass: string;

  @Input() public customSearch = false;

  @Input() public set setSelectedNetPingDeviceIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }
    if (
      ids?.length &&
      ((this._selectedNetPingDevices?.length &&
        !this._selectedNetPingDevices?.every((selectedNetPingDevice) => ids.includes(selectedNetPingDevice.id))) ||
        !this._selectedNetPingDevices?.length)
    ) {
      let newIds = ids.filter((id) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === id));
      if (newIds?.length) {
        this.netPingDeviceService
          .findMany({ includeIds: newIds, limit: newIds?.length > 500 ? 500 : newIds?.length, offset: 0 })
          .subscribe({
            next: (data) => {
              this.netPingDevices = this.netPingDevices.concat(
                data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id))
              );
              this.cdRef.markForCheck();
              setTimeout(() => {
                this._selectedNetPingDevices = this.netPingDevices.filter((netPingDevice) => ids.includes(netPingDevice.id));
                this.cdRef.markForCheck();
              }, 30);
            }
          });
      } else {
        this._selectedNetPingDevices = this.netPingDevices.filter((netPingDevice) => ids.includes(netPingDevice.id));
      }
    }
  }

  public constructor(
    private readonly netPingDeviceService: NetPingDeviceService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (
      !this.customSearch &&
      this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.PHYSICAL_ACCESS_CONTROL?.enabled
    ) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.netPingDeviceService.findMany(this.queryParams).subscribe({
        next: (data) => {
          this.netPingDevices = this.netPingDevices?.length
            ? this.netPingDevices.concat(
                data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id))
              )
            : data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(netPingDevices: NetPingDeviceFindInterface[]) {
    this.selectedNetPingDevicesChange.emit(netPingDevices);
  }

  public onShowPanel() {
    if (
      !this.customSearch &&
      this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.PHYSICAL_ACCESS_CONTROL?.enabled
    ) {
      setTimeout(() => {
        this.multiSelectComponent?.multiSelect?.scroller.onScroll.subscribe(() => {
          let el: HTMLElement = this.multiSelectComponent?.multiSelect?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.netPingDevices.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.netPingDeviceService.findMany(this.queryParams).subscribe({
                next: (data) => {
                  this.netPingDevices = this.netPingDevices.concat(
                    data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (
      !this.customSearch &&
      this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.PHYSICAL_ACCESS_CONTROL?.enabled
    ) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.netPingDeviceService.findMany(this.queryParams).subscribe({
        next: (data) => {
          this.netPingDevices = data.items.concat(
            this._selectedNetPingDevices?.filter(
              (selectedNetPingDevice) => !data.items.some((item) => item.id === selectedNetPingDevice.id)
            )
          );
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
